import useIsOutOfTop from '@flight-workspace/common-client/src/hooks/useIsOutOfTop';
import { PopularFlightBizMore as SvgMore } from 'asset/svg';
import classNames from 'classnames/bind';
import { IPromotion } from 'common/promotion';
import { useCurationContext } from 'contexts/curation';
import { InternationalContext } from 'contexts/international';
import { SeatClass, TripType, getBizFlightUrl } from 'contexts/searchParams';
import { CurationLabel } from 'hooks/useCuration';
import { Fragment, useCallback, useContext, useRef, useState } from 'react';
import ConcurrentItemContainer from './concurrent/item';
import styles from './index.module.scss';
import IndivisualItem from './indivisual/item';
import {
  IFareTypeList,
  IInternationalContextResult,
  IQueryValue,
  ISchedules,
  SchFaresMap,
  SelectTypeKeys,
} from './types';
import { getFareKeys, getPriceOfPerson } from './utils';

const cx = classNames.bind(styles);
const nClickKey = 'isp';
const DEFAULT_POPULAR_FLIGHT_NUMBER = 2;

interface IProps {
  flightResult: IInternationalContextResult;
  isOnLoading: boolean;
  promotions: IPromotion[];
  queryVariables: IQueryValue;
  selectedFlightList: string[][];
  tripType: TripType;
  selectFlight: (
    index: number,
    sch: string,
    fareType: string,
    airline: string,
    labels: string[],
  ) => void;
  selectFlights: (
    schs: string,
    fareType: string,
    airline: string,
    labels: string[],
  ) => void;
}

const getMinPrice = (fares: IFareTypeList) =>
  Math.min(
    ...Object.values(fares)
      .flat()
      .map(fare => getPriceOfPerson(fare.Adult, true)),
  );

const filterSch = (
  sch: string,
  schedules: ISchedules[],
  fare?: IFareTypeList,
) => {
  if (!fare) {
    return false;
  }
  const schKeys = sch.split('|');
  if (
    schKeys.length !== schedules.length ||
    schKeys.some((key, index) => !schedules[index][key])
  ) {
    return false;
  }
  return true;
};

/**
 * 특정 curation에 해당되는 스케줄을 원하는 개수만큼만 필터링하는 함수
 * @param {string} schs 큐레이션에 해당되는지 판단하고자 하는 스케줄들이 담긴 배열
 * @param {(sch: string) => boolean} isCuration 특정 큐레이션 판단 함수
 * @param {number} maxNumber 필터링하고자 하는 개수
 * @return {string[]} 특정 curation이 원하는 개수만큼만 존재하도록 필터링된 스케줄 배열
 */
const filterCuration = (
  schs: string[],
  isCuration: (sch: string) => boolean,
  maxNumber: number,
): string[] => {
  const curationSchs = schs
    ?.filter(sch => isCuration(sch))
    ?.slice(0, maxNumber);
  return schs?.filter(sch => !isCuration(sch) || curationSchs?.includes(sch));
};

const sortByPrice = (
  beforeSch: string,
  afterSch: string,
  fares: SchFaresMap,
) => {
  const beforeFare = fares.get(beforeSch)!;
  const afterFare = fares.get(afterSch)!;
  return getMinPrice(beforeFare) - getMinPrice(afterFare);
};

const PopularFlight = ({
  flightResult,
  isOnLoading,
  promotions,
  queryVariables,
  selectedFlightList,
  tripType,
  selectFlight,
  selectFlights,
}: IProps) => {
  const isTripTypeRT = tripType === TripType.RT;
  const scrollRef = useRef<HTMLDivElement>(null);
  const [visibleRef, isOutOfTop] = useIsOutOfTop(isOnLoading, 0.1);
  const {
    isCurationTarget,
    isTopBooking,
    isTopClick,
    isMinDuration,
    isMinPrice,
    isNonstopMinPrice,
    getLabels,
  } = useCurationContext();
  const { schs, fares, schedules = [] } = flightResult;
  const { sortSchs } = useContext(InternationalContext);
  const selectedFareType =
    selectedFlightList.length > 0
      ? selectedFlightList[selectedFlightList.length - 1][1]
      : '';
  const hasNonstopMinPrice = schs.some(
    sch => !isMinPrice(sch) && isNonstopMinPrice(sch),
  );
  const sortedSchs = sortSchs(
    schs.filter(
      sch =>
        isCurationTarget(sch) &&
        // 전체 스케줄 내에 직항 최처자 라벨 스케줄이 존재하고, 특정 스케줄이 예약 많은 라벨이나 많이 찾는 라벨이 없으면서 최저가 라벨을 보유 중일 때 해당 스케줄 미노출
        // https://oss.navercorp.com/techminds/bts/issues/1883#issuecomment-14246172
        (hasNonstopMinPrice && !isTopBooking(sch) && !isTopClick(sch)
          ? !isMinPrice(sch)
          : true),
    ),
    SelectTypeKeys.concurrent,
    flightResult,
  );

  const sortSch = useCallback(
    (beforeSch: string, afterSch: string) => {
      // 정렬 1순위: 예약 많은, 많이 찾는 라벨 유무
      if (isTopBooking(beforeSch) !== isTopBooking(afterSch)) {
        return isTopBooking(beforeSch) ? -1 : 1;
      }
      if (isTopClick(beforeSch) !== isTopClick(afterSch)) {
        return isTopClick(beforeSch) ? -1 : 1;
      }

      // 정렬 2순위: 라벨 개수 (단, 직항 최저가와 최저가는 중복이므로 제외)
      const getLabelsWithoutDupNonstop = (sch: string) => {
        const labels = getLabels(sch);
        if (labels.has(CurationLabel.MIN_PRICE)) {
          labels.delete(CurationLabel.NONSTOP_MIN_PRICE);
        }
        return labels;
      };

      const labelSizeDiff =
        getLabelsWithoutDupNonstop(afterSch).size -
        getLabelsWithoutDupNonstop(beforeSch).size;
      if (labelSizeDiff !== 0) {
        return labelSizeDiff;
      }

      // 정렬 3순위: 가격
      return sortByPrice(beforeSch, afterSch, fares);
    },
    [isTopBooking, isTopClick],
  );

  const popularSchs = isTripTypeRT
    ? filterCuration(
        sortedSchs ? sortedSchs?.map(sch => sch[0])?.filter(e => e) : [],
        isMinDuration,
        3,
      )
        .map(e => [e])
        .filter(schArr => {
          const [sch] = schArr;
          const fare = fares.get(sch);
          return filterSch(sch, schedules, fare);
        })
        .sort((before, after) => {
          const [beforeSch] = before;
          const [afterSch] = after;
          return sortSch(beforeSch, afterSch);
        })
    : filterCuration(schs, isMinDuration, 3)
        .filter(sch => {
          if (
            !isCurationTarget(sch) ||
            // 전체 스케줄 내에 직항 최처자 라벨 스케줄이 존재하고, 특정 스케줄이 예약 많은 라벨이나 많이 찾는 라벨이 없으면서 최저가 라벨을 보유 중일 때 해당 스케줄 미노출
            // https://oss.navercorp.com/techminds/bts/issues/1883#issuecomment-14246172
            (hasNonstopMinPrice &&
              !isTopBooking(sch) &&
              !isTopClick(sch) &&
              isMinPrice(sch))
          ) {
            return false;
          }
          const fare = fares.get(sch);
          return filterSch(sch, schedules, fare);
        })
        .sort((beforeSch, afterSch) => sortSch(beforeSch, afterSch));

  const isSchsExposable = isTripTypeRT
    ? (popularSchs as string[][])?.some(
        ([sch]) => isTopBooking(sch) || isTopClick(sch),
      )
    : (popularSchs as string[])?.some(
        sch => isTopBooking(sch) || isTopClick(sch),
      );

  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const [isListShowAll, setIsListShowAll] = useState(false);

  const topSchs = isTripTypeRT
    ? (popularSchs as string[][]).slice(0, DEFAULT_POPULAR_FLIGHT_NUMBER)
    : (popularSchs as string[]).slice(0, DEFAULT_POPULAR_FLIGHT_NUMBER);

  const detailSchs = isTripTypeRT
    ? (popularSchs as string[][])
        .slice(DEFAULT_POPULAR_FLIGHT_NUMBER)
        .sort((before, after) => {
          const [beforeSch] = before;
          const [afterSch] = after;
          return sortByPrice(beforeSch, afterSch, fares);
        })
    : (popularSchs as string[])
        .slice(DEFAULT_POPULAR_FLIGHT_NUMBER)
        .sort((before, after) => sortByPrice(before, after, fares));

  const handleScroll = (popularFlightRef: React.RefObject<HTMLDivElement>) => {
    popularFlightRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
  };

  const bizflightUrl = getBizFlightUrl({
    itinerary: queryVariables.itinerary,
    passengers: {
      adult: queryVariables.adult,
      child: queryVariables.child,
      infant: queryVariables.infant,
    },
    seatClass: queryVariables.fareType as SeatClass,
    stayLength: queryVariables.stayLength,
    isDirect: queryVariables.isDirect ?? false,
  });

  if (!isOnLoading && popularSchs.length < 1) {
    return null;
  }

  // '예약 많은', '많이 찾는' 라벨이 없는 경우 인기 항공편 영역 전체 미노출
  if (!isOnLoading && !isSchsExposable) {
    return null;
  }

  return (
    <div className={cx('PopularFlight')} ref={visibleRef}>
      <div className={cx('inner')}>
        <div
          className={cx('title_area', isOnLoading && 'loading')}
          ref={scrollRef}
        >
          <h3 className={cx('title')}>인기 항공편</h3>
          <button
            className={cx('info')}
            type="button"
            aria-label="인기 항공편 기준 안내"
            aria-expanded={isTooltipOpen}
            data-event-nlog="click"
            data-event-area-code={`${nClickKey}.info`}
            onClick={() => {
              setIsTooltipOpen(prev => !prev);
            }}
          />
          <div className={cx('tooltip')}>
            <em>인기 항공편 기준</em>
            <p className={cx('txt')}>
              검색한 항공편 중 가격, 비행시간, 유저 선호도 등 다양한 요소를 고려
              <span className={cx('divider')} />
              • 최저가, 직항 최저가 : 검색 결과 중 가장 저렴한 항공편
              <br />
              • 최단 비행시간 : 검색 결과 중 총 비행시간이 가장 짧은 항공편
              <br />
              • 많이 찾는 : 검색 결과 중 유저의 클릭이 가장 많은 항공편
              <br />• 예약 많은 : 검색 결과 중 유저의 예약이 가장 많은 항공편
            </p>
            <button
              className={cx('close')}
              type="button"
              aria-label="툴팁 닫기"
              data-event-nlog="click"
              data-event-area-code={`${nClickKey}.infoclose`}
              onClick={() => {
                setIsTooltipOpen(false);
              }}
            />
          </div>
          {isOnLoading && (
            <div
              className={cx('loading_area')}
              aria-label="Loading..."
              aria-busy="true"
            />
          )}
          <a
            href={bizflightUrl}
            className={cx('biz_more')}
            data-event-nlog="click"
            data-event-area-code={`${nClickKey}.gobizflight`}
            target="_blank"
          >
            출장 인기 항공편·비자 보기
            <SvgMore className={cx('ico')} />
          </a>
        </div>
        {popularSchs.length >= 1 && isSchsExposable && (
          <>
            <div className={cx('popular_flight_list')}>
              {isTripTypeRT ? (
                <>
                  {(topSchs as string[][]).map((schArr, index) => {
                    const [sch, ...otherItems] = schArr;
                    const fare = fares.get(sch)!;
                    const fareKeys = getFareKeys(fare);
                    const [depSch, arrSch] = sch.split('|');
                    const [
                      { detail: depDetail, journeyTime: depTime },
                      { detail: arrDetail, journeyTime: arrTime },
                    ] = [schedules[0][depSch], schedules[1][arrSch]];
                    return (
                      <Fragment key={sch}>
                        <ConcurrentItemContainer
                          fare={fare}
                          fareKeys={fareKeys}
                          flightResult={flightResult}
                          isTopPopularFlight={
                            index < DEFAULT_POPULAR_FLIGHT_NUMBER
                          }
                          journeyTimes={[depTime, arrTime]}
                          nClickKey={nClickKey}
                          otherItems={otherItems}
                          promotions={promotions}
                          sch={sch}
                          schedule={[depDetail, arrDetail]}
                          selectFlights={selectFlights}
                          showHelpTooltip={!index}
                        />
                      </Fragment>
                    );
                  })}
                  {isListShowAll &&
                    (detailSchs as string[][]).map((schArr, index) => {
                      const [sch, ...otherItems] = schArr;
                      const fare = fares.get(sch)!;
                      const fareKeys = getFareKeys(fare);
                      const [depSch, arrSch] = sch.split('|');
                      const [
                        { detail: depDetail, journeyTime: depTime },
                        { detail: arrDetail, journeyTime: arrTime },
                      ] = [schedules[0][depSch], schedules[1][arrSch]];
                      return (
                        <Fragment key={sch}>
                          <ConcurrentItemContainer
                            fare={fare}
                            fareKeys={fareKeys}
                            flightResult={flightResult}
                            isTopPopularFlight={
                              index < DEFAULT_POPULAR_FLIGHT_NUMBER
                            }
                            journeyTimes={[depTime, arrTime]}
                            nClickKey={nClickKey}
                            otherItems={otherItems}
                            promotions={promotions}
                            sch={sch}
                            schedule={[depDetail, arrDetail]}
                            selectFlights={selectFlights}
                          />
                        </Fragment>
                      );
                    })}
                </>
              ) : (
                <>
                  {(topSchs as string[]).map((sch, index) => {
                    const fare = fares.get(sch)!;
                    const fareKeys = selectedFareType
                      ? [selectedFareType]
                      : getFareKeys(fare);
                    const targetSch = sch.split('|')[selectedFlightList.length];
                    const { journeyTime } =
                      schedules[selectedFlightList.length][targetSch];
                    const scheduleDetails = sch
                      .split('|')
                      .map((key, index) => schedules[index][key].detail);
                    return (
                      <Fragment key={targetSch}>
                        <IndivisualItem
                          fare={fare}
                          fareKeys={fareKeys}
                          flightResult={flightResult}
                          isTopPopularFlight={
                            index < DEFAULT_POPULAR_FLIGHT_NUMBER
                          }
                          journeyTime={journeyTime}
                          nClickKey={nClickKey}
                          promotions={promotions}
                          sch={sch}
                          scheduleId={targetSch}
                          scheduleIndex={selectedFlightList.length}
                          schedules={scheduleDetails}
                          selectedFareType={selectedFareType}
                          tripType={tripType}
                          selectFlight={(
                            sch: string,
                            fareType: string,
                            airline: string,
                            labels: string[],
                          ) =>
                            selectFlight(
                              selectedFlightList.length,
                              sch,
                              fareType,
                              airline,
                              labels,
                            )
                          }
                          showHelpTooltip={!index}
                        />
                      </Fragment>
                    );
                  })}
                  {isListShowAll &&
                    (detailSchs as string[]).map((sch, index) => {
                      const fare = fares.get(sch)!;
                      const fareKeys = selectedFareType
                        ? [selectedFareType]
                        : getFareKeys(fare);
                      const targetSch =
                        sch.split('|')[selectedFlightList.length];
                      const { journeyTime } =
                        schedules[selectedFlightList.length][targetSch];
                      const scheduleDetails = sch
                        .split('|')
                        .map((key, index) => schedules[index][key].detail);
                      return (
                        <Fragment key={targetSch}>
                          <IndivisualItem
                            fare={fare}
                            fareKeys={fareKeys}
                            flightResult={flightResult}
                            isTopPopularFlight={
                              index < DEFAULT_POPULAR_FLIGHT_NUMBER
                            }
                            journeyTime={journeyTime}
                            nClickKey={nClickKey}
                            promotions={promotions}
                            sch={sch}
                            scheduleId={targetSch}
                            scheduleIndex={selectedFlightList.length}
                            schedules={scheduleDetails}
                            selectedFareType={selectedFareType}
                            tripType={tripType}
                            selectFlight={(
                              sch: string,
                              fareType: string,
                              airline: string,
                              labels: string[],
                            ) =>
                              selectFlight(
                                selectedFlightList.length,
                                sch,
                                fareType,
                                airline,
                                labels,
                              )
                            }
                          />
                        </Fragment>
                      );
                    })}
                </>
              )}
            </div>
            {!isListShowAll &&
              popularSchs.length > DEFAULT_POPULAR_FLIGHT_NUMBER && (
                <div className={cx('btn_area')}>
                  <button
                    className={cx('more')}
                    type="button"
                    aria-expanded={false}
                    data-event-nlog="click"
                    data-event-area-code={`${nClickKey}.schedulemore1`}
                    onClick={() => {
                      setIsListShowAll(true);
                    }}
                  >
                    <i className={cx('count')}>
                      <b className={cx('value')}>
                        {popularSchs.length - DEFAULT_POPULAR_FLIGHT_NUMBER}
                      </b>
                      <span className={cx('unit')}>개</span>
                    </i>
                    인기 항공편 더보기
                  </button>
                </div>
              )}
          </>
        )}
      </div>
      {!isOnLoading &&
        popularSchs.length >= 1 &&
        isSchsExposable &&
        isOutOfTop && (
          <button
            className={cx('button_popular_flight')}
            onClick={() => handleScroll(scrollRef)}
          >
            인기 항공편 보기
          </button>
        )}
    </div>
  );
};

export default PopularFlight;
